import { Component, Input, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular';
import { AdWatchOffer, AppConfigService } from 'src/app/app-config.service';
import { ProductItem, ProductType } from 'src/app/purchase/productList';
import { freeTier, bronzeTier, silverTier, goldTier } from 'src/app/productStore.service';

interface TierPlan {
  id: string;
  label: string;
  priceNote: string;
}

interface TierInfo {
  id: 'free' | 'bronze' | 'silver' | 'gold';
  title: string;
  subtitle: string;
  comingSoon?: boolean;
  hasAds: boolean;
  boards: string[];
  instruments: string[];
  features: string[];
  plans: TierPlan[];
}

@Component({
  selector: 'pay-wall',
  styleUrls: ['pay-wall.scss'],
  templateUrl: 'pay-wall.html'
})
export class PayWallComponent {
  @Input() productCatalog: ProductItem[] = [];
  @Input() focusSku = '';

  constructor(
    private modalController: ModalController,
    public appConfig: AppConfigService
  ) {}

  tiers: TierInfo[] = [];

  loadingAd = false;
  selectedTierId: TierInfo['id'] = 'gold';
  selectedPlanByTier: Record<string, string> = {
    free: 'free',
    bronze: 'bronze-3m',
    silver: 'silver-3m',
    gold: 'gold-3m'
  };
  expandedSections: Record<string, boolean> = {};
  readonly previewCount = 3;
  videoUrl = 'https://youtube.com/playlist?list=PLc2oWikp61rVwJ71XxIe3iSA0tlwNGUIL';

  ngOnInit(): void {
    this.tiers = this.buildTierViews();
    const focusedTier = this.findTierBySku(this.focusSku);
    if (focusedTier) {
      this.selectedTierId = focusedTier.id;
    }
  }

  access48Illigible() {
    let instDate;
    if (this.appConfig.buildInfo) {
      instDate = this.appConfig.buildInfo.installDate;
    }
    if (instDate) {
      const delta = Date.now() - Date.parse(instDate);
      if (delta < 24 * 3600 * this.appConfig.freeDays * 1000) {
        return true;
      }
    }
    return false;
  }

  freeaccess() {
    this.modalController.dismiss({ action: 'freeaccess' });
  }

  subscription() {
    this.subscriptionFor(this.selectedTierId, this.getSelectedPlan(this.selectedTierId));
  }

  subscriptionFor(tierId: TierInfo['id'], planId: string) {
    const tier = this.tiers.find((item) => item.id === tierId);
    if (!tier || tier.id === 'free' || tier.comingSoon) {
      return;
    }
    this.modalController.dismiss({ action: 'purchase', tierId, planId });
  }

  selectTier(tierId: TierInfo['id']): void {
    this.selectedTierId = tierId;
  }

  selectPlan(tierId: TierInfo['id'], planId: string): void {
    this.selectedTierId = tierId;
    this.selectedPlanByTier[tierId] = planId;
  }

  getSelectedPlan(tierId: TierInfo['id']): string {
    return this.selectedPlanByTier[tierId] || '';
  }

  getSelectedPlanNote(tier: TierInfo): string {
    const selectedPlanId = this.getSelectedPlan(tier.id);
    const selectedPlan = tier.plans.find((plan) => plan.id === selectedPlanId);
    return selectedPlan?.priceNote || '';
  }

  private buildTierViews(): TierInfo[] {
    const boardNameMap = this.buildBoardNameMap();
    const instrumentNameMap = this.buildInstrumentNameMap();

    const tierSources: Array<{ id: TierInfo['id']; source: any; subtitle: string; comingSoon?: boolean }> = [
      { id: 'free', source: freeTier, subtitle: 'Basic access with ads' },
      { id: 'bronze', source: bronzeTier, subtitle: 'Entry premium' },
      { id: 'silver', source: silverTier, subtitle: 'Performance tier' },
      { id: 'gold', source: goldTier, subtitle: 'Future premium release', comingSoon: true }
    ];

    return tierSources.map((tierSource) => {
      const source = tierSource.source || {};
      const boards = (source.boardSku || []).map((sku: string) => this.lookupName(sku, boardNameMap));
      const instruments = (source.instrumentsSku || []).map((sku: string) =>
        this.lookupName(sku, instrumentNameMap)
      );
      const features = (source.features || []).map((feature: any) => String(feature));

      return {
        id: tierSource.id,
        title: this.cleanTierName(source.tierName || tierSource.id),
        subtitle: tierSource.subtitle,
        comingSoon: !!tierSource.comingSoon,
        hasAds: !!(source.bannerads || source.interAds),
        boards,
        instruments,
        features,
        plans:
          tierSource.id === 'free'
            ? [{ id: 'free', label: 'Current Plan', priceNote: 'No subscription' }]
            : [
                { id: `${tierSource.id}-3m`, label: '3 Months', priceNote: 'Quarterly plan' },
                { id: `${tierSource.id}-6m`, label: '6 Months', priceNote: 'Popular option' },
                { id: `${tierSource.id}-12m`, label: '12 Months', priceNote: 'Best value yearly' }
              ]
      };
    });
  }

  private buildBoardNameMap(): Map<string, string> {
    const map = new Map<string, string>();
    (this.productCatalog || [])
      .filter((item) => item.type === ProductType.Board || item.type === ProductType.WesternKeyboard)
      .forEach((item) => {
        const name = item.data?.title || item.desciption || item.sku;
        if (item.sku && name) {
          map.set(item.sku, name);
        }
      });
    return map;
  }

  private buildInstrumentNameMap(): Map<string, string> {
    const map = new Map<string, string>();
    (this.productCatalog || [])
      .filter((item) => item.type === ProductType.InstrumentSound)
      .forEach((item) => {
        const name = item.data?.displayName || item.data?.title || item.desciption || item.sku;
        if (item.sku && name) {
          map.set(item.sku, name);
        }
      });
    return map;
  }

  private lookupName(sku: string, sourceMap: Map<string, string>): string {
    if (!sku) {
      return '';
    }
    const fromMap = sourceMap.get(sku);
    if (fromMap) {
      return fromMap;
    }
    return this.prettifySku(sku);
  }

  private prettifySku(sku: string): string {
    const cleaned = sku
      .replace(/-webfont$/i, '')
      .replace(/-sarega$/i, '')
      .replace(/-real$/i, '')
      .replace(/--/g, '-')
      .replace(/,/g, '')
      .replace(/-/g, ' ')
      .trim();
    return cleaned
      .split(' ')
      .filter((w) => !!w)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  private cleanTierName(name: string): string {
    return String(name || '')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private findTierBySku(sku: string): TierInfo | undefined {
    if (!sku) {
      return undefined;
    }
    return this.tiers.find((tier) => {
      const source =
        tier.id === 'free' ? freeTier : tier.id === 'bronze' ? bronzeTier : tier.id === 'silver' ? silverTier : goldTier;
      return (source?.boardSku || []).includes(sku) || (source?.instrumentsSku || []).includes(sku);
    });
  }

  isExpanded(tierId: string, section: 'boards' | 'instruments' | 'features'): boolean {
    return !!this.expandedSections[`${tierId}-${section}`];
  }

  toggleExpanded(tierId: string, section: 'boards' | 'instruments' | 'features'): void {
    const key = `${tierId}-${section}`;
    this.expandedSections[key] = !this.expandedSections[key];
  }

  visibleItems(tier: TierInfo, section: 'boards' | 'instruments' | 'features'): string[] {
    const list = tier[section] || [];
    if (this.isExpanded(tier.id, section)) {
      return list;
    }
    return list.slice(0, this.previewCount);
  }

  get webplatform(): boolean {
    return Capacitor.getPlatform() === 'web';
  }

  showVideo() {
    window.open(this.videoUrl, '_blank', 'location=yes');
  }

  videoAd() {
    this.loadingAd = true;
    this.modalController.dismiss({ action: 'videoAd' });
  }

  get freeAdTime() {
    return AdWatchOffer;
  }

  exitApp() {
    this.modalController.dismiss({ action: 'exit' });
  }

  navHome() {
    this.modalController.dismiss({ action: 'navHome' });
  }
}
